/**
 * Subscription Manager
 *
 * Coordinates all chat subscriptions and prevents duplicates
 */

import { logger } from "@/utils/logger";
import {
  MessageSubscription,
  MessageSubscriptionConfig,
} from "./messageSubscription";
import {
  ConversationSubscription,
  ConversationSubscriptionConfig,
} from "./conversationSubscription";
import {
  NotificationSubscription,
  NotificationSubscriptionConfig,
} from "./notificationSubscription";
import {
  ConnectionMonitor,
  ConnectionMonitorCallbacks,
} from "./connectionMonitor";

export class SubscriptionManager {
  private messageSubscription: MessageSubscription | null = null;
  private conversationSubscription: ConversationSubscription | null = null;
  private notificationSubscription: NotificationSubscription | null = null;
  private connectionMonitor: ConnectionMonitor | null = null;

  /**
   * Set up message subscription for a conversation
   */
  setupMessageSubscription(config: MessageSubscriptionConfig): void {
    // Clean up existing subscription
    if (this.messageSubscription) {
      this.messageSubscription.cleanup();
    }

    try {
      this.messageSubscription = new MessageSubscription(config);
      this.messageSubscription.subscribe();
    } catch (error) {
      // Catch any errors during subscription setup (e.g., WebSocket initialization)
      logger.warn(
        "Failed to setup message subscription",
        { component: "SubscriptionManager" },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Set up conversation list subscription
   */
  setupConversationSubscription(config: ConversationSubscriptionConfig): void {
    // Clean up existing subscription
    if (this.conversationSubscription) {
      this.conversationSubscription.cleanup();
    }

    try {
      this.conversationSubscription = new ConversationSubscription(config);
      this.conversationSubscription.subscribe();
    } catch (error) {
      // Catch any errors during subscription setup (e.g., WebSocket initialization)
      logger.warn(
        "Failed to setup conversation subscription",
        { component: "SubscriptionManager" },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Set up notification subscription
   */
  setupNotificationSubscription(config: NotificationSubscriptionConfig): void {
    // Clean up existing subscription
    if (this.notificationSubscription) {
      this.notificationSubscription.cleanup();
    }

    try {
      this.notificationSubscription = new NotificationSubscription(config);
      this.notificationSubscription.subscribe();
    } catch (error) {
      // Catch any errors during subscription setup (e.g., WebSocket initialization)
      logger.warn(
        "Failed to setup notification subscription",
        { component: "SubscriptionManager" },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Set up connection monitoring
   */
  setupConnectionMonitor(callbacks: ConnectionMonitorCallbacks = {}): void {
    // Clean up existing monitor
    if (this.connectionMonitor) {
      this.connectionMonitor.stop();
    }

    try {
      this.connectionMonitor = new ConnectionMonitor(callbacks);
      this.connectionMonitor.start();
    } catch (error) {
      // Catch any errors during connection monitor setup (e.g., WebSocket initialization)
      logger.warn(
        "Failed to setup connection monitor",
        { component: "SubscriptionManager" },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Clean up message subscription
   */
  cleanupMessageSubscription(): void {
    if (this.messageSubscription) {
      this.messageSubscription.cleanup();
      this.messageSubscription = null;
    }
  }

  /**
   * Clean up conversation subscription
   */
  cleanupConversationSubscription(): void {
    if (this.conversationSubscription) {
      this.conversationSubscription.cleanup();
      this.conversationSubscription = null;
    }
  }

  /**
   * Clean up notification subscription
   */
  cleanupNotificationSubscription(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.cleanup();
      this.notificationSubscription = null;
    }
  }

  /**
   * Clean up all subscriptions
   */
  cleanup(): void {
    if (this.messageSubscription) {
      this.messageSubscription.cleanup();
      this.messageSubscription = null;
    }

    if (this.conversationSubscription) {
      this.conversationSubscription.cleanup();
      this.conversationSubscription = null;
    }

    if (this.notificationSubscription) {
      this.notificationSubscription.cleanup();
      this.notificationSubscription = null;
    }

    if (this.connectionMonitor) {
      this.connectionMonitor.stop();
      this.connectionMonitor = null;
    }

    logger.debug("All subscriptions cleaned up", {
      component: "SubscriptionManager",
    });
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): "connected" | "connecting" | "disconnected" | "error" {
    return this.connectionMonitor?.getStatus() || "disconnected";
  }

  /**
   * Check if message subscription is active
   */
  isMessageSubscriptionActive(): boolean {
    return this.messageSubscription?.isActive() || false;
  }
}
